import { FormProvider } from '@slal/ui/react-hook-form/core';
import { Field, FieldSet, RadioFieldGroup } from '@slal/ui/react-hook-form/fields';
import { Article, Heading, P } from '@slal/ui/typography';
import { MaxWidthContent, StyledForm } from '~/components';
import { ApplicationButtons } from '~/features';
import { MainPlanOptions } from '~/types';
import { useKeyConsiderationsApplicationForm } from './hooks';
import { mainPlanOptions } from './utils';

const SpendOptionContent = () => {
    return (
        <Article>
            <Heading level={5}>Enjoy, it's your money and of course you can do whatever you like with it.</Heading>
            <P>
                It's important to remember that the main aim of a pension is to provide you with a comfortable life when
                you want to stop working. Taking money from your pension now could have an impact on this.
            </P>
            <P>
                Did you know that pensions are one of the best place to invest your money because they're really tax
                efficient? It might seem obvious to say that taking your money out now means you'll have less for later
                but did you know you won't benefit from any investment growth on that money as well? This means that you
                could be losing out on more than you first thought. Pension values can go down as well as up. You could
                get back less than you paid in.
            </P>
            <P>
                If you have money elsewhere, you could consider using non-pension assets first as on death these may be
                subject to inheritance tax. On the other hand, normally your pension can go to your partner, family or
                friends inheritance tax-free. And if you don't have a specific plan for what you're spending this
                withdrawal on, consider whether you need to take it out now.
            </P>
        </Article>
    );
};

const SaveOptionContent = () => {
    return (
        <Article>
            <Heading level={5}>OK, it's good to have financial security.</Heading>
            <P>
                It's important to remember that the main aim of a pension is to provide you with a comfortable life when
                you want to stop working. Taking money from your pension now could have an impact on this.
            </P>
            <P>
                Did you know that pensions are one of the best place to invest your money because they're really tax
                efficient and, now that you're over 55, you have complete and easy access to your pension money at any
                time. Also, when you die your remaining pension can normally go to your family or friends free of
                inheritance tax, whereas, if you take out money to invest elsewhere they could then potentially be
                subject to inheritance tax. You may wish to consider using other savings and investments first.
            </P>
            <P markdown>
                Check that anywhere you are investing your money is regulated by the Financial Conduct Authority before
                you agree to anything.Be aware of pension scams and don't feel rushed into making any decisions. Visit
                the [Pensions Regulator](https://www.thepensionsregulator.gov.uk/pension-scams) for more information on
                pension scams.
            </P>
        </Article>
    );
};

const ReduceDebtOptionContent = () => {
    return (
        <Article>
            <Heading level={5}>OK, it's normally sensible to pay off debts.</Heading>
            <P>
                However, if repaying debt early means paying penalties then it may not be worth it. While it's usually a
                good idea to pay off debts, you could consider using non-pension assets first because on death these may
                be subject to inheritance tax.
            </P>
            <P>
                Make sure you understand all your options for managing your debts as there may be a better option than
                using your pension. And please be aware that any money you take from your pension could be used by
                creditors to pay off any outstanding debts if you don't meet your repayments.
            </P>
            <P markdown>
                It's also important to remember that the main aim of a pension is to provide you with a comfortable life
                when you want to stop working. Taking money from your pension now could have an impact on this. You can
                get information on how to access free impartial advice on managing debts from the
                [MoneyHelper](https://www.moneyhelper.org.uk/en?source=mas).
            </P>
            <P markdown>
                Did you know you can seek additional help from organisations and charities for debt [counselling and
                support](https://www.standardlife.co.uk/help/accessibility#additional-support)?
            </P>
        </Article>
    );
};

const LiveOptionContent = () => {
    return (
        <Article>
            <Heading level={5}>OK, it's good you've got a plan.</Heading>
            <P markdown>
                If you're aiming to use this withdrawal to pay for everyday items then it's really important that you're
                comfortable with how long it's going to last. Consider taking guidance from [Pension
                Wise](https://www.moneyhelper.org.uk/en/pensions-and-retirement/pension-wise?source=pw), a service from
                MoneyHelper or obtaining advice from a financial adviser.
            </P>
            <P>
                You could consider using other assets or investments before using a pension pot as on death these may be
                subject to inheritance tax. On the other hand, your pension can normally go to your partner, family or
                friends inheritance tax-free.
            </P>
            <P markdown>
                Your remaining pension will stay invested, giving potential for future investment growth which can help
                your income last longer, but this also means the value of your pension could drop if markets fall. You
                could run out of money. You need to be comfortable with this when withdrawing from your pension. If
                complete peace of mind is what you're looking for, you should consider setting up a guaranteed income
                for life (an annuity) instead. Make sure you read the [MoneyHelper
                guide](https://library.standardlife.co.uk/mas_guide.pdf) for information on all your pension access
                options.
            </P>
        </Article>
    );
};

const SelectedPlanContent = ({ option }: { option: MainPlanOptions }) => {
    switch (option) {
        case MainPlanOptions.SPEND:
            return <SpendOptionContent />;
        case MainPlanOptions.SAVE:
            return <SaveOptionContent />;
        case MainPlanOptions.REDUCE_DEBT:
            return <ReduceDebtOptionContent />;
        case MainPlanOptions.LIVE:
            return <LiveOptionContent />;
        default:
            return null;
    }
};

export const MainPlanForm = () => {
    const { form, handleSubmit, name, option } = useKeyConsiderationsApplicationForm(
        'mainPlan',
        '/application/key-considerations/health-issues'
    );

    return (
        <MaxWidthContent>
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit}>
                    <FieldSet aria-label="Main plan options">
                        <Field errorFor={name}>
                            <RadioFieldGroup name={name} options={mainPlanOptions} stacked />
                        </Field>
                    </FieldSet>
                    {option && <SelectedPlanContent option={option} />}
                    <ApplicationButtons backTo="/application/key-considerations/information" />
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};
